// Crop helper module for handling image cropping functionality
export default class CropHelper {
  constructor(options = {}) {
    this.initialCoverage = options.initialCoverage || 1;
    this.maxWidth = options.maxSize || 240;
    this.maxHeight = options.aspectRatio
      ? Math.round(this.maxWidth / options.aspectRatio)
      : this.maxWidth;
    this.aspectRatio = options.aspectRatio || 1;
    this.cropModal = document.getElementById(options.modalId || 'crop-modal');
    this.cropperContainer = document.getElementById(
      options.containerId || 'cropper-container'
    );
    this.closeCropModalButton = document.getElementById(
      options.closeButtonId || 'close-crop-modal'
    );
    this.cropDoneButton = document.getElementById(
      options.doneButtonId || 'crop-done-button'
    );

    // Bind methods
    this.showCropModal = this.showCropModal.bind(this);
    this.closeCropModal = this.closeCropModal.bind(this);
    this.cropDone = this.cropDone.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);

    // Setup event listeners
    this._setupEventListeners();

    // Callback functions & state
    this.onCropComplete = options.onCropComplete || null;
    this.onModalClose = options.onModalClose || null;
    this.previewElement = options.previewElement || null;

    this.originalImageData = null;
    this.originalImageWidth = null;
    this.originalImageHeight = null;
    this.currentSelectionDetail = null;
    this.cropperCanvas = null;
    this.cropperImage = null;
    this.cropperSelection = null;
  }

  _setupEventListeners() {
    if (this.closeCropModalButton) {
      this.closeCropModalButton.addEventListener('click', this.closeCropModal);
    }
    if (this.cropDoneButton) {
      this.cropDoneButton.addEventListener('click', this.cropDone);
    }
    if (this.cropModal) {
      this.cropModal.addEventListener('close', this.handleModalClose);
    }
  }

  showCropModal(imageData) {
    const img = new Image();
    img.onload = () => {
      this.originalImageWidth = img.naturalWidth;
      this.originalImageHeight = img.naturalHeight;
      this.originalImageData = imageData;

      this.cropModal.showModal();

      // Delay setup slightly for modal transition
      setTimeout(() => {
        this._setupCropperUI();
      }, 300);
    };
    img.onerror = () => {
      console.error('Failed to load image for cropping.');
    };
    img.src = imageData;
  }

  _setupCropperUI() {
    if (!this.cropperContainer || !this.originalImageData) return;

    this.cropperContainer.innerHTML = '';
    this._createCropperElements();

    if (this.cropperSelection) {
      this.cropperSelection.addEventListener('change', (event) => {
        this.currentSelectionDetail = event.detail; // Store latest selection
        this._enforceSelectionMaxSize(event); // Enforce max size if needed
      });
    }

    // Set src after elements are in DOM
    if (this.cropperImage) {
      this.cropperImage.src = this.originalImageData;
    }
  }

  _createCropperElements() {
    const cropperCanvas = document.createElement('cropper-canvas');
    const cropperImage = document.createElement('cropper-image');
    const cropperSelection = document.createElement('cropper-selection');

    // Configure Image
    cropperImage.alt = 'Image to crop';
    cropperImage.setAttribute('rotatable', '');
    cropperImage.setAttribute('scalable', '');
    cropperImage.setAttribute('translatable', '');

    // Configure Selection
    cropperSelection.setAttribute('resizable', '');
    cropperSelection.setAttribute('keyboard', '');
    cropperSelection.setAttribute('movable', '');
    cropperSelection.setAttribute('precise', '');
    cropperSelection.setAttribute(
      'initial-coverage',
      String(this.initialCoverage)
    );
    cropperSelection.setAttribute('aspect-ratio', String(this.aspectRatio));
    cropperSelection.style.maxWidth = `${this.maxWidth}px`;
    cropperSelection.style.maxHeight = `${this.maxHeight}px`;

    // Add Grid
    const cropperGrid = document.createElement('cropper-grid');
    cropperGrid.setAttribute('covered', '');
    cropperSelection.appendChild(cropperGrid);

    // Add Handles
    const handleThemeColor = 'rgba(255, 255, 255, 0.5)';
    const handleActions = [
      'move',
      'n-resize',
      'e-resize',
      's-resize',
      'w-resize',
      'ne-resize',
      'nw-resize',
      'se-resize',
      'sw-resize',
    ];
    handleActions.forEach((action) => {
      const handle = document.createElement('cropper-handle');
      handle.setAttribute('action', action);
      handle.setAttribute('theme-color', handleThemeColor);
      cropperSelection.appendChild(handle);
    });

    // Assemble Cropper
    cropperCanvas.appendChild(cropperImage);
    cropperCanvas.appendChild(cropperSelection);
    this.cropperContainer.appendChild(cropperCanvas);

    // Store references
    this.cropperCanvas = cropperCanvas;
    this.cropperImage = cropperImage;
    this.cropperSelection = cropperSelection;
  }

  _enforceSelectionMaxSize(event) {
    const selection = event.detail;
    if (selection.width > this.maxWidth || selection.height > this.maxHeight) {
      event.preventDefault();
      const aspectRatio = this.aspectRatio;
      let newWidth, newHeight;
      if (selection.width > this.maxWidth) {
        newWidth = this.maxWidth;
        newHeight = this.maxWidth / aspectRatio;
      } else {
        newHeight = this.maxHeight;
        newWidth = this.maxHeight * aspectRatio;
      }
      this.cropperSelection?.$change(
        selection.x,
        selection.y,
        newWidth,
        newHeight
      );
    }
  }

  handleModalClose() {
    this.cropperCanvas = null;
    this.cropperImage = null;
    this.cropperSelection = null;
    this.originalImageData = null;
    this.originalImageWidth = null;
    this.originalImageHeight = null;
    this.currentSelectionDetail = null;
    if (typeof this.onModalClose === 'function') {
      this.onModalClose();
    }
  }

  closeCropModal() {
    if (this.cropModal) {
      this.cropModal.close();
    }
  }

  cropDone() {
    const data = this._getRequiredCropData();
    if (!data) {
      this.closeCropModal();
      return;
    }

    const sourceRect = this._calculateClampedSourceRect(data);
    if (!sourceRect || sourceRect.width <= 0 || sourceRect.height <= 0) {
      console.error('Invalid source rectangle calculated.');
      this.closeCropModal();
      return;
    }

    this._drawAndGenerateOutput(sourceRect);
  }

  _getRequiredCropData() {
    if (
      !this.cropperSelection ||
      !this.cropperImage ||
      !this.cropperCanvas ||
      !this.currentSelectionDetail ||
      !this.originalImageData ||
      !this.originalImageWidth ||
      !this.originalImageHeight
    ) {
      console.error(
        'Cropping cannot proceed: missing essential data or elements.'
      );
      return null;
    }

    const selection = this.currentSelectionDetail;
    const displayedRect = this.cropperImage.getBoundingClientRect();
    const canvasRect = this.cropperCanvas.getBoundingClientRect();

    if (
      !displayedRect ||
      displayedRect.width === 0 ||
      displayedRect.height === 0 ||
      !canvasRect
    ) {
      console.error(
        'Cropper image or canvas dimensions not available or are zero.'
      );
      return null;
    }

    return { selection, displayedRect, canvasRect };
  }

  _calculateClampedSourceRect({ selection, displayedRect, canvasRect }) {
    const displayedWidth = displayedRect.width;
    const displayedHeight = displayedRect.height;

    if (displayedWidth === 0 || displayedHeight === 0) return null;

    const scaleX = this.originalImageWidth / displayedWidth;
    const scaleY = this.originalImageHeight / displayedHeight;

    const imageOffsetX = displayedRect.left - canvasRect.left;
    const imageOffsetY = displayedRect.top - canvasRect.top;

    const relativeSelectionX = selection.x - imageOffsetX;
    const relativeSelectionY = selection.y - imageOffsetY;

    let sx = relativeSelectionX * scaleX;
    let sy = relativeSelectionY * scaleY;
    let sWidth = selection.width * scaleX;
    let sHeight = selection.height * scaleY;

    // Clamp source rectangle
    sx = Math.max(0, sx);
    sy = Math.max(0, sy);
    sWidth = Math.min(sWidth, this.originalImageWidth - sx);
    sHeight = Math.min(sHeight, this.originalImageHeight - sy);
    sWidth = Math.max(0, sWidth);
    sHeight = Math.max(0, sHeight);

    return { sx, sy, width: sWidth, height: sHeight };
  }

  _drawAndGenerateOutput(sourceRect) {
    const outputWidth = Math.round(sourceRect.width);
    const outputHeight = Math.round(sourceRect.height);

    if (outputWidth <= 0 || outputHeight <= 0) {
      console.error('Calculated output dimensions are zero or negative.');
      this.closeCropModal();
      return;
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;
    const ctx = outputCanvas.getContext('2d');

    const originalImage = new Image();
    originalImage.onload = () => {
      try {
        ctx.drawImage(
          originalImage,
          sourceRect.sx,
          sourceRect.sy,
          sourceRect.width,
          sourceRect.height,
          0, // Destination X
          0, // Destination Y
          outputWidth,
          outputHeight
        );
        this._generateBlobsAndComplete(outputCanvas);
      } catch (drawError) {
        console.error('Error drawing cropped image onto canvas:', drawError);
        this.closeCropModal();
      }
    };
    originalImage.onerror = (error) => {
      console.error('Error loading original image for drawing:', error);
      this.closeCropModal();
    };
    originalImage.src = this.originalImageData;
  }

  _generateBlobsAndComplete(outputCanvas) {
    let mimeType = 'image/png';
    let quality = undefined;
    if (this.originalImageData.startsWith('data:image/jpeg')) {
      mimeType = 'image/jpeg';
      quality = 0.92; // High quality JPEG
    }

    outputCanvas.toBlob(
      (croppedBlob) => {
        if (!croppedBlob) {
          console.error('Failed to generate cropped blob.');
          this.closeCropModal();
          return;
        }
        const croppedDataUrl = outputCanvas.toDataURL(mimeType, quality);

        if (this.previewElement) {
          this.previewElement.src = croppedDataUrl;
        }

        this.dataUrlToBlob(this.originalImageData)
          .then((originalBlob) => {
            if (typeof this.onCropComplete === 'function') {
              this.onCropComplete(croppedBlob, croppedDataUrl, originalBlob);
            }
          })
          .catch((error) => {
            console.error(
              'Error converting original image dataURL to Blob:',
              error
            );
            if (typeof this.onCropComplete === 'function') {
              this.onCropComplete(croppedBlob, croppedDataUrl, null);
            }
          })
          .finally(() => {
            this.closeCropModal();
          });
      },
      mimeType,
      quality
    );
  }

  // Helper function to convert Data URL to Blob
  async dataUrlToBlob(dataUrl) {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return blob;
  }

  destroy() {
    if (this.closeCropModalButton) {
      this.closeCropModalButton.removeEventListener(
        'click',
        this.closeCropModal
      );
    }
    if (this.cropDoneButton) {
      this.cropDoneButton.removeEventListener('click', this.cropDone);
    }
    if (this.cropModal) {
      this.cropModal.removeEventListener('close', this.handleModalClose);
    }
    this.cropperContainer.innerHTML = ''; // Clear generated elements
    this.handleModalClose(); // Reset state variables
  }
}
